import { CubePresenter, Logger, NOOP_VOID } from 'wdc-cube';
import { CompanyApi } from '../companies.api';
import { CompaniesKeys } from '../companies.key';
import { Places } from '../../../../route-consts';
import { CompanyConfirmKeys } from './company-confirm.key';
import { CompanyConfirmScope } from './company-confirm.scope';
import { blockedAttemptScopes } from '../blocked-attempt.scope';
const LOG = Logger.get('Mormaii.CompanyConfirmPresenter');
// @Inject
const companyApi = CompanyApi.INSTANCE;
/**
 * A confirmacao de bloquear ou remover uma empresa.
 *
 * Um place, entao o Voltar cancela e um reload nao deixa a acao meio feita.
 *
 * Vale registrar o que isto conserta: no painel antigo, **"Remover Empresa" apaga
 * sem perguntar nada** - clique direto num item de menu, logo abaixo de "Bloquear
 * Acesso", que pergunta. A acao mais destrutiva das duas era a unica sem
 * confirmacao. Nao e desenho, e descuido, entao aqui as duas confirmam.
 */
export class CompanyConfirmPresenter extends CubePresenter {
    constructor(app) {
        super(app, new CompanyConfirmScope());
        this.dialogSlot = NOOP_VOID;
        /**
         * Qual confirmacao esta aberta.
         *
         * Chamado `mode` e nao `action`: `CubePresenter.action` e o guarda de acoes do
         * framework, e um campo com esse nome o sombrearia - `this.action(...)` deixaria
         * de existir e todo handler da classe cairia junto.
         */
        this.mode = 'block';
        /** Token das tentativas: a resposta de outra empresa, aberta antes, fica calada. */
        this.pending = 0;
    }
    async applyParameters(intent, initialization) {
        const keys = new CompanyConfirmKeys(this.app, intent);
        if (!keys.target) {
            // Endereco sem alvo (versao anterior, ou editado): volta para a lista, com o
            // filtro que veio no intent em transito.
            LOG.error('Confirmação sem empresa alvo');
            await this.app.flipToIntent(intent.redirect(Places.companies));
            return false;
        }
        // Atributos da intencao: lidos a cada chamada, como os slots. Vem de quem esta
        // acima na composicao, e reler custa nada.
        this.list = keys.list;
        if (initialization) {
            this.scope.onConfirm = this.action(this.onConfirm);
            this.scope.onClose = this.action(this.onClose);
            LOG.info('Initialized');
        }
        // O slot e decidido acima na composicao e pode mudar entre viagens: le-se sempre.
        this.dialogSlot = keys.dialogSlot;
        this.companyId = keys.target;
        this.mode = keys.action;
        // A empresa vem da lista, que a tem em maos - como la, onde o modal recebe
        // o objeto inteiro por prop.
        const company = this.list?.companyOf(this.companyId);
        this.scope.cnpj = company?.cnpj ?? '';
        this.scope.razaoSocial = company?.razaoSocial ?? company?.nomeFantasia ?? '';
        this.scope.nomeFantasia = company?.nomeFantasia ?? '';
        this.scope.ramoAtividade = company?.ramoAtividade ?? '';
        this.scope.contactName = company?.contactName ?? '';
        this.scope.contactPhone = company?.contactPhone ?? '';
        this.scope.contactEmail = company?.contactEmail ?? '';
        this.scope.kind = this.mode;
        this.scope.attempts = [];
        if (this.mode === 'delete') {
            // Sem par na referencia: la o item de menu apaga sem perguntar. O
            // dialogo e o mesmo do bloqueio, com o texto da remocao.
            this.scope.title = 'Remover Empresa';
            this.scope.question = 'Deseja remover definitivamente a empresa selecionada?';
            this.scope.explanation = '';
            this.scope.confirmLabel = 'Sim, Remover Empresa';
        }
        else if (this.mode === 'unblock') {
            // Decisao do cliente de 08/10/2026: liberar tambem pede confirmacao.
            this.scope.title = 'Liberar Acesso';
            this.scope.question = 'Deseja liberar o acesso da empresa selecionada?';
            this.scope.explanation =
                'Os administradores e profissionais da empresa voltam a entrar no painel. ' +
                    'O administrador principal passa a ver as tentativas de acesso feitas durante o bloqueio.';
            this.scope.confirmLabel = 'Sim, Liberar Acesso';
        }
        else {
            this.scope.title = 'Bloquear Acesso';
            this.scope.question = 'Deseja bloquear o acesso da empresa selecionada?';
            // O que o bloqueio faz, e o que ele NAO faz - o texto anterior prometia
            // "bloquear o acesso" e a empresa so sumia das listas.
            this.scope.explanation =
                'Os administradores e profissionais que estão só nesta empresa deixam de entrar no painel, ' +
                    'e quem estiver dentro sai na próxima ação. O app dos colaboradores continua funcionando, ' +
                    'e as medições continuam sendo guardadas. As tentativas de entrar no painel ficam registradas.';
            this.scope.confirmLabel = 'Sim, Bloquear Acesso';
        }
        this.dialogSlot(this.scope);
        // As tentativas correm em paralelo: o dialogo abre na hora, e a lista chega.
        if (this.mode === 'unblock') {
            this.loadAttempts(this.companyId).catch((caught) => this.app.unexpected('Carregando as tentativas de acesso', caught));
        }
        return true;
    }
    /**
     * O que o endereco diz enquanto este dialogo esta aberto.
     *
     * Sem isto os parametros do dialogo DESAPARECEM da URL no primeiro write: o
     * framework reconstroi o endereco perguntando a cada presenter vivo o que ele
     * mostra, e quem nao responde some. O sintoma e cruel - o dialogo abre certo,
     * porque o intent do flip tinha os parametros, e so o endereco fica errado; um
     * reload entao cai num dialogo sem alvo.
     */
    publishParameters(intent) {
        const keys = new CompanyConfirmKeys(this.app, intent);
        keys.target = this.companyId;
        keys.action = this.mode;
    }
    async onConfirm() {
        if (this.companyId === undefined) {
            return;
        }
        this.scope.busy = true;
        try {
            // Titulo e mensagem sao os do painel antigo, letra por letra.
            if (this.mode === 'delete') {
                await companyApi.deleteCompany(this.companyId);
                this.app.alert('info', 'Sucesso', 'Empresa deletada com sucesso');
            }
            else if (this.mode === 'unblock') {
                await companyApi.activateCompany(this.companyId);
                this.app.alert('info', 'Acesso Liberado', 'O acesso da empresa selecionada foi liberado.');
            }
            else {
                await companyApi.deactivateCompany(this.companyId);
                this.app.alert('info', 'Acesso Bloqueado', 'O acesso da empresa selecionada foi bloqueado com sucesso!');
            }
            await this.list?.reload();
            await this.close();
        }
        catch (caught) {
            // La o erro abre o modal global, e nao aparece dentro do dialogo.
            this.app.alert('error', 'Erro', caught instanceof Error ? caught.message : 'Não foi possível concluir.');
        }
        finally {
            this.scope.busy = false;
        }
    }
    async loadAttempts(companyId) {
        const token = ++this.pending;
        this.scope.loadingAttempts = true;
        try {
            const attempts = await companyApi.fetchBlockedSignInAttempts(companyId);
            if (token !== this.pending) {
                return;
            }
            this.scope.attempts = blockedAttemptScopes(attempts, this.update);
        }
        finally {
            if (token === this.pending) {
                this.scope.loadingAttempts = false;
            }
        }
    }
    async onClose() {
        await this.close();
    }
    /**
     * Fecha o dialogo voltando para a lista, com o filtro que ela tem.
     *
     * Os filtros NAO sao copiados campo a campo: `CompaniesKeys.of` cria o intent
     * da lista e o framework pede a cada presenter vivo o que ele mostra - e o
     * presenter da lista esta vivo, porque este dialogo e um place dentro dele.
     */
    async close() {
        await CompaniesKeys.of(this.app).flip();
    }
}
//# sourceMappingURL=company-confirm.presenter.js.map